import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, LogOut, Search, ChevronDown, ChevronUp, Mail, Phone, AlertCircle } from 'lucide-react';
import './AdminInquiries.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'hs_admin_token';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_) {
    return String(iso);
  }
};

const AdminInquiries = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [tokenInput, setTokenInput] = useState('');
  const [authError, setAuthError] = useState('');

  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all'); // all | new | checked
  const [search, setSearch] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  const fetchInquiries = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setLoadError('');
    try {
      const res = await fetch(`${API_BASE}/api/contact?limit=500`, {
        headers: { 'X-Admin-Token': token },
      });
      if (res.status === 401) {
        setToken('');
        sessionStorage.removeItem(TOKEN_KEY);
        setAuthError('認証に失敗しました。トークンをご確認ください。');
        setInquiries([]);
        return;
      }
      if (!res.ok) {
        setLoadError('データの取得に失敗しました。');
        return;
      }
      const data = await res.json();
      setInquiries(Array.isArray(data) ? data : []);
    } catch (_) {
      setLoadError('通信エラーが発生しました。');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    const t = tokenInput.trim();
    sessionStorage.setItem(TOKEN_KEY, t);
    setToken(t);
    setTokenInput('');
    setAuthError('');
  };

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken('');
    setInquiries([]);
    setExpandedId(null);
  };

  const toggleStatus = async (row) => {
    const next = row.status === 'checked' ? 'new' : 'checked';
    setUpdatingId(row.id);
    // Optimistic update
    setInquiries((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: next } : r)));
    try {
      const res = await fetch(`${API_BASE}/api/contact/${row.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Token': token },
        body: JSON.stringify({ status: next }),
      });
      if (!res.ok) {
        // Revert on failure
        setInquiries((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: row.status } : r)));
      }
    } catch (_) {
      setInquiries((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: row.status } : r)));
    } finally {
      setUpdatingId(null);
    }
  };

  const filtered = useMemo(() => {
    let list = inquiries;
    if (filter !== 'all') list = list.filter((r) => (r.status || 'new') === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((r) =>
        [r.name, r.company, r.email, r.phone, r.message].some(
          (v) => v && String(v).toLowerCase().includes(q),
        ),
      );
    }
    return list;
  }, [inquiries, filter, search]);

  const counts = useMemo(() => {
    const total = inquiries.length;
    const nw = inquiries.filter((r) => (r.status || 'new') === 'new').length;
    return { total, nw, checked: total - nw };
  }, [inquiries]);

  if (!token) {
    return (
      <div className="admin-page">
        <div className="admin-login-card">
          <h1 className="admin-title">管理者ログイン</h1>
          <p className="admin-sub">お問い合わせ管理画面にアクセスするには、管理トークンを入力してください。</p>
          <form onSubmit={handleLogin} className="admin-login-form">
            <input
              type="password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder="管理トークン"
              className="admin-login-input"
              autoFocus
              data-testid="admin-token-input"
            />
            <button type="submit" className="admin-btn admin-btn-primary" data-testid="admin-login-btn">
              ログイン
            </button>
          </form>
          {authError && (
            <div className="admin-alert" data-testid="admin-auth-error">
              <AlertCircle size={16} />
              <span>{authError}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-shell">
        <header className="admin-header">
          <div>
            <h1 className="admin-title">お問い合わせ管理</h1>
            <p className="admin-sub">
              合計 <strong>{counts.total}</strong> 件 &nbsp;·&nbsp;
              未対応 <strong className="admin-pill admin-pill-new">{counts.nw}</strong> &nbsp;·&nbsp;
              対応済 <strong className="admin-pill admin-pill-checked">{counts.checked}</strong>
            </p>
          </div>
          <div className="admin-actions">
            <button
              type="button"
              onClick={fetchInquiries}
              className="admin-btn admin-btn-ghost"
              disabled={loading}
              data-testid="admin-refresh-btn"
            >
              <RefreshCw size={15} className={loading ? 'is-spinning' : ''} />
              更新
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="admin-btn admin-btn-ghost"
              data-testid="admin-logout-btn"
            >
              <LogOut size={15} />
              ログアウト
            </button>
          </div>
        </header>

        <div className="admin-toolbar">
          <div className="admin-filter-tabs" role="tablist">
            {[
              { key: 'all', label: `すべて (${counts.total})` },
              { key: 'new', label: `未対応 (${counts.nw})` },
              { key: 'checked', label: `対応済 (${counts.checked})` },
            ].map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setFilter(t.key)}
                className={`admin-tab ${filter === t.key ? 'is-active' : ''}`}
                data-testid={`admin-filter-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="admin-search">
            <Search size={15} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="名前・会社・メール・本文で検索"
              data-testid="admin-search-input"
            />
          </div>
        </div>

        {loadError && (
          <div className="admin-alert" data-testid="admin-load-error">
            <AlertCircle size={16} />
            <span>{loadError}</span>
          </div>
        )}

        <div className="admin-table-wrap" data-testid="admin-inquiries-table">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="col-date">受信日時</th>
                <th className="col-name">お名前</th>
                <th className="col-company">会社名</th>
                <th className="col-email">メール</th>
                <th className="col-phone">電話</th>
                <th className="col-message">ご相談内容</th>
                <th className="col-status">ステータス</th>
              </tr>
            </thead>
            <tbody>
              {loading && inquiries.length === 0 && (
                <tr>
                  <td colSpan="7" className="admin-empty">読み込み中...</td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan="7" className="admin-empty">
                    {inquiries.length === 0
                      ? 'お問い合わせはまだありません。'
                      : '該当する結果がありません。'}
                  </td>
                </tr>
              )}
              {filtered.map((row) => {
                const status = row.status || 'new';
                const isOpen = expandedId === row.id;
                return (
                  <tr
                    key={row.id}
                    className={`admin-row ${status === 'new' ? 'is-new' : ''}`}
                    data-testid={`admin-row-${row.id}`}
                  >
                    <td className="col-date">{formatDate(row.created_at)}</td>
                    <td className="col-name">{row.name}</td>
                    <td className="col-company">{row.company || '—'}</td>
                    <td className="col-email">
                      <a href={`mailto:${row.email}`} className="admin-inline-link">
                        <Mail size={13} />
                        {row.email}
                      </a>
                    </td>
                    <td className="col-phone">
                      {row.phone ? (
                        <a href={`tel:${row.phone.replace(/[^0-9+]/g, '')}`} className="admin-inline-link">
                          <Phone size={13} />
                          {row.phone}
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="col-message">
                      <button
                        type="button"
                        className="admin-message-toggle"
                        onClick={() => setExpandedId(isOpen ? null : row.id)}
                        data-testid={`admin-message-toggle-${row.id}`}
                      >
                        <span className={`admin-message-preview ${isOpen ? 'is-open' : ''}`}>{row.message}</span>
                        {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                    </td>
                    <td className="col-status">
                      <button
                        type="button"
                        className={`admin-status-btn admin-pill admin-pill-${status}`}
                        onClick={() => toggleStatus(row)}
                        disabled={updatingId === row.id}
                        data-testid={`admin-status-btn-${row.id}`}
                      >
                        {status === 'new' ? '未対応' : '対応済'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminInquiries;
